<template>
  <li v-if="levels.length > 0" class="tw-relative tw-top-0 tw-left-0">
    <div class="sf-accordion-head" @click.prevent="expanded = !expanded">
      <div class="tw-absolute tw-top-[8px] tw-left-[22px]">
        <span v-if="expanded">-</span>
        <span v-else>+</span>
      </div>
      <div class="tw-pl-4">
        {{ category.name }} ({{ yearLabel }})
      </div>
    </div>
    <div>
      <SfAccordion :show="expanded" @transtionend="$emit('resized')">
        <ul class="tw-pl-4">
          <DbMathLevelItem
            v-for="c in subCategories"
            :key="c.id"
            :category="c"
            :all-categories="allCategories"
            :all-levels="allLevels"
            :students="students"
            :teacher-mode="teacherMode"
            :current-user="currentUser"
            :default-year="year"
            :hide-empty="true"
          />
        </ul>
        <div class="tw-pl-4">
          <table class="sf-table">
            <tbody>
              <tr v-if="teacherMode">
                <td></td>
                <td v-for="sid in activeStudentIds" :key="sid">
                  {{ students[sid].fullName }}
                </td>
              </tr>
              <template v-for="lvl in levels" :key="lvl.id">
                <tr>
                  <td>
                    <div>
                      <SfRouterLink
                        v-if="!teacherMode"
                        :to="'/maths/level/' + lvl.id"
                      >
                        {{ lvl.name }}
                      </SfRouterLink>
                      <span v-else>
                        {{ lvl.name }}
                      </span>
                    </div>
                    <div v-if="lvl.subLevels && lvl.subLevels.length > 0">
                      <a href="" @click.prevent="lvl.expanded = !lvl.expanded">
                        + {{ lvl.sublevels_expand_label || 'smaller steps' }}
                      </a>
                    </div>
                  </td>
                  <td v-for="sid in activeStudentIds" :key="sid">
                    <StudentResultSummary
                      v-if="lvl.studentResultSummary"
                      :summary="lvl.studentResultSummary[sid]"
                    />
                  </td>
                </tr>
                <template v-if="lvl.expanded">
                  <tr v-for="sublvl in lvl.subLevels" :key="sublvl.id">
                    <td class="!tw-pl-6">
                      <SfRouterLink
                        v-if="!teacherMode"
                        :to="'/maths/level/' + sublvl.id"
                      >
                        {{ sublvl.name }}
                      </SfRouterLink>
                      <span v-else>
                        {{ sublvl.name }}
                      </span>
                    </td>
                    <td v-for="sid in activeStudentIds" :key="sid">
                      <StudentResultSummary
                        v-if="sublvl.studentResultSummary"
                        :summary="sublvl.studentResultSummary[sid]"
                      />
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="showYearPaging" class="tw-text-center">
          <div v-if="prevYear" class="tw-inline-block tw-px-4">
            <a href="" @click.prevent="currentYear = prevYear"> &lt; Easier </a>
          </div>
          <div v-if="nextYear" class="tw-inline-block tw-px-4">
            <a href="" @click.prevent="currentYear = nextYear"> Harder &gt; </a>
          </div>
        </div>
      </SfAccordion>
    </div>
  </li>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import SfRouterLink from "@/components/shared/SfRouterLink.vue";
import {
  SfMathsLevelExpandable,
  SfMathsLevelCategory,
} from "@/types/mathsTypes";
import SfAccordion from "@/components/ui/SfAccordion.vue";
import { Dict, SfUser } from "@/types/generalTypes";
import StudentResultSummary from "../StudentResultSummary.vue";
import userHasRole from "@/func/user/userHasRole";

export default defineComponent({
  components: { SfRouterLink, SfAccordion, StudentResultSummary },
  name: "DbMathLevelItem",
  props: {
    currentUser: { 
      type: [Object, null] as PropType<SfUser | null>, 
      required: true 
    },
    category: {
      type: Object as PropType<SfMathsLevelCategory>,
      required: true,
    },
    allCategories: {
      type: Object as PropType<SfMathsLevelCategory[]>,
      required: true,
    },
    allLevels: {
      type: Object as PropType<SfMathsLevelExpandable[]>,
      required: true,
    },
    students: { type: Object as PropType<Dict<SfUser>>, required: true },
    defaultYear: { type: Number, default: 1 },
    teacherMode: Boolean,
    showYearPaging: { type: Boolean, default: false },
    hideEmpty: { type: Boolean, default: false },
  },
  data() {
    return {
      expanded: false,
      currentYear: null as number | null,
    };
  },
  computed: {
    subCategories(): SfMathsLevelCategory[] {
      return this.allCategories.filter(
        (c) => c.parent_category_id == this.category.id,
      );
    },
    categoryLevels() {
      return this.allLevels.filter((lvl) => {
        return lvl.math_level_category_id == this.category.id;
      });
    },
    levels(): SfMathsLevelExpandable[] {
      return this.categoryLevels.filter((lvl) => {
        return lvl.year == this.year;
      });
    },
    isHidden(): boolean {
      return (
        this.hideEmpty && this.subCategories.length + this.levels.length < 1
      );
    },
    activeStudentIds(): number[] {
      if (!this.teacherMode && !userHasRole(this.currentUser, "teacher")) {
        return this.currentUser ? [this.currentUser.id] : [];
      }
      const res: number[] = [];
      for (const lvl of this.levels) {
        for (let i in lvl.studentResultSummary) {
          const sId = Number(i);
          if (!res.includes(sId)) {
            res.push(sId);
          }
        }
      }
      return res;
    },
    year(): number {
      return this.currentYear ? this.currentYear : this.defaultYear;
    },
    yearLabel() { 
      if (userHasRole(this.currentUser, "teacher")) {
        return 'year ' + this.year;
      } 
      return this.year;
    },
    prevYear() {
      let res = null;
      for (const lvl of this.categoryLevels) {
        if (lvl.year < this.year) {
          if (res === null || lvl.year > res) {
            res = lvl.year;
          }
        }
      }
      return res;
    },
    nextYear() {
      let res = null;
      for (const lvl of this.categoryLevels) {
        if (lvl.year > this.year) {
          if (res === null || lvl.year < res) {
            res = lvl.year;
          }
        }
      }
      return res;
    },
  },
});
</script>
